//@ts-nocheck 
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { putRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { showToast } from "@banno/jha-wc/src/js/utils/toast-util.js";
import { GlobalVariables } from "../../utils/constants";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { resolveAuthFlowErrorMessage } from "../../utils/user-account-login-lock";
import { credentialConfirmationUserType, isBusinessUnitAdminUserType, isEnterpriseAdminUserType } from "../../utils/app-utils";
import { removeAllCookies } from "../../utils/removeAllCookies";
import {
  getCredentialHistoryPersistWarning,
  isCredentialUpdateSuccess,
} from "../../utils/credential-history-warning";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/icons/jha-icon-show";
import "../../Components/Loader";

@customElement("set-password")
export class SetPassword extends formElementMixin(LitElement) {
  constructor() {
    super();
  }

  @property({ type: Number }) passwordMinLength = GlobalVariables.USER_PASSWORD_MIN_LENGTH;

  @state() formData = {
    userId: "",
    verificationCode: "",
    newPassword: "",
    confirmPassword: "",
    sshKeyValue: "",
  };

  // Password validation states
  @state() public atLeastMinLength = false;
  @state() public atLeastOneLetter = false;
  @state() public atLeastOneNumber = false;
  @state() public atLeastOneUpperCase = false;
  @state() public atLeastOneLowerCase = false;
  @state() public atLeastOneSpecial = false;
  @state() public cannotBeginOrEndInSpaces = false;
  @state() public newPassAndConfirmPassAreSame = false;
  @property({ type: Boolean }) setConfirm = false;
  /** From set-password / set-SSH success response; drives Participant vs admin confirmation. */
  @state() confirmedUserType = "";
  @state() closeBlocked = false;
  @property({ type: Boolean }) apiLoading = false;
  @property({ type: Boolean }) showNewPassword = false;
  @property({ type: Boolean }) showConfirmPassword = false;

  @property({ reflect: true, attribute: "set-type" }) setType = "";

  @property({ type: Object }) SecretSSH: Map<string, string> = new Map();

  @property({ type: Boolean }) setSsh = false;

  @property({ type: Boolean }) validSSHKey = !this.setSsh;

  static get styles() {
    return [
      css`
        p > [icon] {
          margin-right: 8px;
          vertical-align: middle;
        }
        jha-icon-circle-checkmark-filled {
          fill: var(--jha-color-success);
        }
        jha-card {
          width: 508px;
          padding: 48px;
          position: relative;
          border-radius: 23px;
        }
        img {
          width: 112px;
          height: 112px;
        }
        #loginBg {
          background-color: #06185f;
          display: flex;
          justify-content: center;
          width: 100vw;
        }
        jha-form-row {
          margin-top: 10px;
          margin-bottom: 10px;
        }
        #avatarDiv {
          display: flex;
          justify-content: center;
          width: 100%;
        }
        #submitBtn {
          width: 90px;
        }
        .password-requirements {
          margin-bottom: 0.5em;
          margin-top: 0.5em;
        }
        .password-container {
          position: relative;
        }
        .password-toggle {
          position: absolute;
          right: 12px;
          top: 34px;
          cursor: pointer;
          z-index: 10;
          color: #666;
          padding: 4px;
          user-select: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .password-toggle:hover {
          color: #4157ad;
        }
        .password-toggle jha-icon-show {
          width: 20px;
          height: 20px;
        }
      `,
    ];
  }

  private resetAllRequirementStates() {
    this.atLeastMinLength = false;
    this.atLeastOneLetter = false;
    this.atLeastOneNumber = false;
    this.atLeastOneUpperCase = false;
    this.atLeastOneLowerCase = false;
    this.atLeastOneSpecial = false;
    this.cannotBeginOrEndInSpaces = false;
    this.newPassAndConfirmPassAreSame = false;
  }

  private async handleSetSsh() {
    this.SecretSSH = new Map();
    this.SecretSSH.set("sshKey", this.formData.sshKeyValue);
    const sshKeyValue = Object.fromEntries(this.SecretSSH);
    const req = {
      userId: this.formData.userId.trim(),
      verificationCode: this.formData.verificationCode.trim(),
      sshKeyValue: JSON.stringify(sshKeyValue),
    };
    const setSsh = await putRequest<{ userId: string; sshKeyValue: string; verificationCode: string }>(
      Apis.SET_SSH,
      undefined,
      req,
      true
    );

    if (isCredentialUpdateSuccess(setSsh)) {
      this.confirmedUserType = credentialConfirmationUserType(setSsh);
      this.setConfirm = true;
      const historyWarning = getCredentialHistoryPersistWarning(setSsh);
      if (historyWarning) {
        showToast({
          message: historyWarning,
          role: "warning",
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
      }
    } else {
      this.showErrorToast(setSsh?.details);
    }
  }

  private async handleSetPassword() {
    const allPasswordValidations = [
      this.atLeastMinLength,
      this.atLeastOneLetter,
      this.atLeastOneNumber,
      this.atLeastOneUpperCase,
      this.atLeastOneLowerCase,
      this.atLeastOneSpecial,
      this.cannotBeginOrEndInSpaces,
      this.newPassAndConfirmPassAreSame,
    ].every(Boolean);

    if (!allPasswordValidations) {
      showToast({
        message: "New Password Must Contain All Items Listed Below.",
        role: "danger",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
      return;
    }

    const req = {
      userId: this.formData.userId.trim(),
      verificationCode: this.formData.verificationCode.trim(),
      newPassword: this.formData.confirmPassword,
    };
    const setPassword = await putRequest<{
      userId: string;
      newPassword: string;
      verificationCode: string;
    }>(Apis.SET_PASSWORD, undefined, req, true);

    if (isCredentialUpdateSuccess(setPassword)) {
      this.confirmedUserType = credentialConfirmationUserType(setPassword);
      this.setConfirm = true;
      const historyWarning = getCredentialHistoryPersistWarning(setPassword);
      if (historyWarning) {
        showToast({
          message: historyWarning,
          role: "warning",
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
      }
    } else {
      this.showErrorToast(setPassword?.details);
    }
  }

  private showErrorToast(details: any) {
    const errMsg =
      details?.length > 0
        ? resolveAuthFlowErrorMessage(details, getErrorMessage)
        : "Something went wrong, please try again.";
    showToast({
      message: errMsg,
      role: "danger",
      duration: GlobalVariables.TOAST_TIMEOUT,
    });
  }

  private passwordsMatch(newPassword: string, confirmPassword: string): boolean {
    const trimmedNew = newPassword?.trim();
    const trimmedConfirm = confirmPassword?.trim();
    return trimmedNew?.length > 0 && trimmedConfirm?.length > 0 && trimmedNew === trimmedConfirm;
  }

  async onSendLinkClick() {
    this.apiLoading = true;
    try {
      if (this.setSsh) {
        await this.handleSetSsh();
      } else {
        await this.handleSetPassword();
      }
    } catch (error) {
      showToast({
        message: "An unexpected error occurred.",
        role: "danger",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    } finally {
      this.apiLoading = false;
    }
  }

  onNewPassChange(e: { target: { value: string } }) {
    const newPass = e.target.value ?? "";
    this.resetAllRequirementStates();

    this.atLeastMinLength = newPass.length >= this.passwordMinLength;
    this.atLeastOneLetter = /[a-zA-Z]/.test(newPass);
    this.atLeastOneNumber = /[0-9]/.test(newPass);
    this.atLeastOneUpperCase = /[A-Z]/.test(newPass);
    this.atLeastOneLowerCase = /[a-z]/.test(newPass);
    this.atLeastOneSpecial = /[^a-zA-Z0-9]/.test(newPass);
    this.cannotBeginOrEndInSpaces = newPass.length > 0 && !/^\s|\s$/.test(newPass);
    this.newPassAndConfirmPassAreSame = this.passwordsMatch(newPass, this.formData.confirmPassword);

    this.formData = { ...this.formData, newPassword: newPass };
  }

  onConfirmPasswordChange(e: { target: { value: string } }) {
    const confirmPass = e.target.value ?? "";
    this.newPassAndConfirmPassAreSame = this.passwordsMatch(this.formData.newPassword, confirmPass);
    this.formData = { ...this.formData, confirmPassword: confirmPass };
  }

  onCloseClick() {
    window.close();
    this.closeBlocked = !window.closed;
  }

  onLoginClick() {
    // router.go is a soft navigation and leaves the set-password shell up.
    // A leftover admin session (Secure cookie, or the localhost dev token)
    // makes /login render the app instead of the login page.
    removeAllCookies();
    window.location.assign("/login");
  }

  /**
   * Enterprise Admin and Business Unit Admin are the only types that keep Login.
   * A missing or unrecognized userType is the participant/destination screen.
   */
  private get useSftpConfirmation(): boolean {
    return !isEnterpriseAdminUserType(this.confirmedUserType)
      && !isBusinessUnitAdminUserType(this.confirmedUserType);
  }

  onSSHKeyChange(e: { target?: { value?: string }; detail?: unknown }) {
    // Pasted keys usually end in a newline, and ssh-rsa is often separated by more than one space.
    // A failed check disables Send Confirmation, so the click never leaves the form.
    const raw = typeof e?.detail === "string" ? e.detail : e?.target?.value;
    const value = String(raw ?? "").trim();
    this.validSSHKey = /^ssh-rsa\s+[A-Za-z0-9+/=]+(?:\s.*)?$/.test(value);
    this.formData = { ...this.formData, sshKeyValue: value };
  }

  onNewPasswordIconClick() {
    this.showNewPassword = !this.showNewPassword;
  }

  onConfirmPasswordIconClick() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  render() {
    if (this.setConfirm) {
      return this.renderConfirmation();
    }
    return this.renderForm();
  }

  private renderConfirmation() {
    if (this.useSftpConfirmation) {
      return this.renderSftpConfirmation();
    }
    return this.renderAdminLoginConfirmation();
  }

  private renderSftpConfirmation() {
    return html`
      <div id="loginBg">
        <jha-card>
          <div id="avatarDiv">
            <img src="circle-checkmark-filled.png" />
          </div>
          <jha-form-row>
            <div id="avatarDiv">
              <h2>${this.setSsh ? "SSH Key Created Successfully" : "Password Created Successfully"}</h2>
            </div>
          </jha-form-row>
          <jha-form-row>
            <div id="avatarDiv">
              <h3>
                ${this.setSsh
                  ? "Your SSH key has been created successfully and is now ready for use with SFTP connections."
                  : "Your password has been created successfully and is now ready for use with SFTP connections."}
              </h3>
            </div>
          </jha-form-row>
          ${this.closeBlocked
            ? html`
                <jha-form-row>
                  <div id="avatarDiv">
                    <h3>You can close this browser tab.</h3>
                  </div>
                </jha-form-row>
              `
            : undefined}
          <jha-form-row>
            <jha-button data-auto-id="closeBtn" large type="button" sync @click=${this.onCloseClick}>
              Close
            </jha-button>
          </jha-form-row>
        </jha-card>
      </div>
    `;
  }

  private renderAdminLoginConfirmation() {
    return html`
      <div id="loginBg">
        <jha-card>
          <div id="avatarDiv">
            <img src="circle-checkmark-filled.png" />
          </div>
          <jha-form-row>
            <div id="avatarDiv">
              <h2>Confirmation Sent</h2>
            </div>
          </jha-form-row>
          <jha-form-row>
            <div id="avatarDiv">
              <h3>
                ${this.setSsh
                  ? "Your new SSH key was created successfully. You can now login using the key."
                  : "Your new password was created successfully. You can now login."}
              </h3>
            </div>
          </jha-form-row>
          <jha-form-row>
            ${!this.setSsh
              ? html`
                  <jha-button data-auto-id="loginBtn" large type="button" sync @click=${this.onLoginClick}>
                    Login
                  </jha-button>
                `
              : undefined}
          </jha-form-row>
        </jha-card>
      </div>
    `;
  }

  private renderForm() {
    return html`
      ${this.apiLoading ? html`<circle-loader></circle-loader>` : undefined}
      <div id="loginBg">
        <form>
          <jha-card>
            <div id="avatarDiv">
              <img src="/jha-logo.png" alt="JHA" />
            </div>
            <jha-form-row>
              <div id="avatarDiv">
                <h2>${this.setSsh ? "Set SSH Key" : "Set Password"}</h2>
              </div>
            </jha-form-row>
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="userIdInput"
                label="User ID"
                required=""
                warning="User ID Required."
                type="text"
                data-id="userId"
                @change=${this.inputValueChange}
                maxlength="256"
                outline>
              </jha-form-text-input>
            </jha-form-row>
            ${this.setSsh ? this.renderSshKeyInput() : this.renderPasswordInputs()}
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="verificationCodeInput"
                label="Confirmation Code"
                required
                warning="Confirmation Code Required."
                type="text"
                data-id="verificationCode"
                @change=${this.inputValueChange}
                maxlength="256"
                outline>
              </jha-form-text-input>
            </jha-form-row>
            ${this.setSsh ? undefined : this.renderPasswordRequirements()}
            <jha-button
              data-auto-id="submitBtn"
              large
              type="submit"
              ?disabled=${!this.formValid || !this.validSSHKey}
              sync
              @click=${this.onSendLinkClick}
              style="margin-bottom: 20px; margin-top: 20px; display: block">
              Send Confirmation
            </jha-button>
          </jha-card>
        </form>
      </div>
    `;
  }

  private renderSshKeyInput() {
    return html`
      <jha-form-row>
        <jha-form-textarea-input
          placeholder="SSH Key"
          warning="Please Provide SSH Key"
          type="text"
          ?showWarning=${!this.validSSHKey}
          role="textbox"
          label="SSH Key"
          data-id="sshKeyValue"
          data-auto-id="SSHKeyInput"
          outline
          required
          @change=${this.onSSHKeyChange}>
        </jha-form-textarea-input>
      </jha-form-row>
    `;
  }

  private renderPasswordInputs() {
    return html`
      <jha-form-row>
        <div class="password-container">
          <jha-form-input
            data-auto-id="passwordInput"
            label="New Password"
            required
            warning="New Password Required"
            type=${this.showNewPassword ? "text" : "password"}
            outline
            data-id="newPassword"
            @change=${this.onNewPassChange}
            maxlength="60">
          </jha-form-input>
          <div
            class="password-toggle"
            @click=${this.onNewPasswordIconClick}
            title="Click to show/hide password">
            <jha-icon-show></jha-icon-show>
          </div>
        </div>
      </jha-form-row>
      <jha-form-row>
        <div class="password-container">
          <jha-form-input
            data-auto-id="confirmPasswordInput"
            label="Confirm New Password"
            required
            warning="Confirm New Password Required"
            type=${this.showConfirmPassword ? "text" : "password"}
            data-id="confirmPassword"
            @change=${this.onConfirmPasswordChange}
            maxlength="60"
            outline>
          </jha-form-input>
          <div
            class="password-toggle"
            @click=${this.onConfirmPasswordIconClick}
            title="Click to show/hide password">
            <jha-icon-show></jha-icon-show>
          </div>
        </div>
      </jha-form-row>
    `;
  }

  private renderPasswordRequirements() {
    return html`
      <jha-form-row>
        <div>
          <p id="passTitle">Password Requirements:</p>
          ${this.renderRequirement(this.atLeastMinLength, `Must be at least ${this.passwordMinLength} characters.`)}
          ${this.renderRequirement(this.atLeastOneLetter, "Must contain at least one letter.")}
          ${this.renderRequirement(this.atLeastOneNumber, "Must contain at least one number.")}
          ${this.renderRequirement(this.atLeastOneUpperCase, "Must contain at least one uppercase letter.")}
          ${this.renderRequirement(this.atLeastOneLowerCase, "Must contain at least one lowercase letter.")}
          ${this.renderRequirement(this.atLeastOneSpecial, "Must contain at least one special character.")}
          ${this.renderRequirement(this.cannotBeginOrEndInSpaces, "Cannot begin or end with a space.")}
          ${this.renderRequirement(this.newPassAndConfirmPassAreSame, "New Password must be the same as Confirm Password.")}
        </div>
      </jha-form-row>
    `;
  }

  private renderRequirement(isMet: boolean, text: string) {
    return html`
      <p class="password-requirements">
        ${isMet
          ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
          : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
        ${text}
      </p>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "set-password": SetPassword;
  }
}
